import React from 'react';
import { Star, Phone, ArrowRight, Sparkles, MessageCircle } from 'lucide-react';
import { contactInfo } from '../mockData';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1426243665764-abe8f66163dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxzcGlyaXR1YWwlMjBteXN0aWNhbHxlbnwwfHx8fDE3NjkwNDM2OTB8MA&ixlib=rb-4.1.0&q=85"
          alt="Spiritual Background"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black"></div>
      </div>
      
      <div className="absolute top-20 left-10 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
      
      <div className="container mx-auto px-4 py-16 md:py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-8 animate-fadeInUp">
            <div className="relative">
              <svg 
                className="w-32 h-32 md:w-40 md:h-40 animate-float" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor: '#d4af37', stopOpacity: 1}} />
                    <stop offset="50%" style={{stopColor: '#f4e5b8', stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: '#d4af37', stopOpacity: 1}} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <path 
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                  fill="url(#starGradient)" 
                  filter="url(#glow)"
                  className="animate-shimmer"
                />
              </svg>
              <div className="absolute inset-0 bg-yellow-500/20 rounded-full blur-2xl animate-glow"></div>
            </div>
          </div>
          
          <div className="mb-4 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight display-font animate-fadeInUp" style={{animationDelay: '0.2s'}}>
            Marabout Voyant <span className="gold-text">en Belgique</span>
          </h1>
          
          <p className="text-2xl md:text-3xl text-gray-300 mb-6 serif-font animate-fadeInUp" style={{animationDelay: '0.4s'}}>
            Retour de l'être aimé • Envoûtement d'amour • Magie blanche & rouge
          </p>
          
          <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed animate-fadeInUp" style={{animationDelay: '0.6s'}}>
            <span className="text-yellow-500 font-bold">Maître Dia</span>, plus de <span className="text-yellow-500 font-bold">20 ans d'expérience</span> dans les arts mystiques traditionnels africains. 
            <span className="block mt-2 text-yellow-400 font-semibold">Résultats dans la même semaine.</span>
          </p>
          
          <div className="glass rounded-2xl p-4 max-w-2xl mx-auto mb-12 animate-fadeInUp" style={{animationDelay: '0.7s'}}>
            <p className="text-gray-300 text-sm md:text-base">
              <span className="text-green-500 font-bold">⚡ La rapidité de ses résultats est différent des autres marabouts</span>, il est puissant, efficace et discret.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-16 animate-fadeInUp" style={{animationDelay: '0.8s'}}>
            <a 
              href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="luxury-button text-white px-10 py-5 rounded-full font-bold text-lg flex items-center space-x-3 group"
            >
              <MessageCircle size={22} className="group-hover:scale-110 transition-transform" />
              <span>WhatsApp</span>
              <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
            </a>
            <a 
              href={`tel:${contactInfo.phone}`}
              className="glass text-white px-10 py-5 rounded-full font-bold text-lg flex items-center space-x-3 hover:bg-white/10 transition-all border-2 border-yellow-500/50"
            >
              <Phone size={22} />
              <span>{contactInfo.phone}</span>
            </a>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto animate-fadeInUp mb-4" style={{animationDelay: '1s'}}>
            <div className="glass rounded-2xl p-6">
              <div className="text-4xl md:text-5xl font-bold gold-text display-font mb-2">20+</div>
              <div className="text-gray-400">Années d'expérience</div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="text-4xl md:text-5xl font-bold gold-text display-font mb-2">500+</div>
              <div className="text-gray-400">Clients satisfaits</div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="text-3xl md:text-4xl font-bold gold-text display-font mb-2">Efficacité</div>
              <div className="text-gray-400">Prouvée</div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="text-4xl md:text-5xl font-bold gold-text display-font mb-2">24/7</div>
              <div className="text-gray-400">Disponibilité</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
    </section>
  );
};

export default Hero;